import {
  aliasLegacyTenantSlug,
  buildTenantRootPath,
  readTenantSlugFromCurrentUrl,
  readTenantSlugFromPath,
} from './tenant-path';

export type TenantRoutingEntry = {
  id: string;
  name: string;
  slug?: string | null;
};

let cachedTenants: TenantRoutingEntry[] = [];
let cachedSessionTenantId: string | null = null;

/**
 * Keeps slug→id resolution in sync with the latest `/me` payload for non-React
 * callers (e.g. `customFetch` tenant header provider and `getPath` on global
 * authenticated routes such as `/whats-new`).
 */
export function setTenantRoutingContext(
  tenants: TenantRoutingEntry[] | undefined,
  sessionTenantId?: string | null
): void {
  cachedTenants = tenants ?? [];
  cachedSessionTenantId = sessionTenantId ?? null;
}

function tenantList(tenants?: TenantRoutingEntry[]): TenantRoutingEntry[] {
  return tenants ?? cachedTenants;
}

/** Finds a tenant whose `slug` matches the URL segment. */
export function findTenantBySlug(
  tenantSlug: string,
  tenants?: TenantRoutingEntry[]
): TenantRoutingEntry | undefined {
  const aliasedSlug = aliasLegacyTenantSlug(tenantSlug);
  return tenantList(tenants).find(
    (tenant) =>
      tenant.slug != null && aliasLegacyTenantSlug(tenant.slug) === aliasedSlug
  );
}

/** Maps a URL slug to the canonical tenant id, or `null` when unknown. */
export function resolveSlugToTenantId(
  tenantSlug: string,
  tenants?: TenantRoutingEntry[]
): string | null {
  return findTenantBySlug(tenantSlug, tenants)?.id ?? null;
}

/** Returns the slug for URL emission, or `null` when the tenant has no slug. */
export function getTenantSlug(
  tenantId: string,
  tenants?: TenantRoutingEntry[]
): string | null {
  const tenant = tenantList(tenants).find((entry) => entry.id === tenantId);
  return tenant?.slug ? aliasLegacyTenantSlug(tenant.slug) : null;
}

/**
 * Slug for landing redirects (`/` and `/~/:tenantSlug` index).
 *
 * Priority:
 *   1. Valid URL route slug (bookmark / deep link wins)
 *   2. Session tenant (`saas_tenant_id`) when it maps to a membership — primary
 *      bare-`/` memory after tenant switch
 *   3. First membership — recovery when session is missing or invalid
 *
 * Property→tenant inference for legacy tenantless URLs lives in
 * `resolvePropertyAwareTenantSlug` / `LegacyTenantRedirect`, not here.
 */
export function resolveLandingTenantSlug(
  routeSlug: string | null | undefined,
  tenants?: TenantRoutingEntry[],
  sessionTenantId?: string | null
): string | null {
  const list = tenantList(tenants);
  if (routeSlug) {
    const match = findTenantBySlug(routeSlug, list);
    if (match?.slug) return aliasLegacyTenantSlug(match.slug);
  }
  if (sessionTenantId) {
    const sessionSlug = getTenantSlug(sessionTenantId, list);
    if (sessionSlug) return sessionSlug;
  }
  const firstSlug = list[0]?.slug;
  return firstSlug ? aliasLegacyTenantSlug(firstSlug) : null;
}

/**
 * Tenant slug for authenticated in-app navigation (404 home, sidebar logo, access
 * denied). URL slug when it matches a membership; otherwise first membership.
 * Never uses `user.saas_tenant_id`.
 *
 * @param tenants - Membership list from `/me`; falls back to routing context cache.
 * @param pathname - When provided (e.g. from `useLocation`), reads slug from this path
 *   instead of `window.location` (preferred in React Router contexts).
 */
export function resolveNavigationTenantSlug(
  tenants?: TenantRoutingEntry[],
  pathname?: string | null
): string | null {
  const routeSlug =
    pathname != null
      ? readTenantSlugFromPath(pathname)
      : readTenantSlugFromCurrentUrl();
  return resolveLandingTenantSlug(routeSlug, tenants);
}

/**
 * Active tenant slug for link emission and API headers.
 *
 * URL slug when present; otherwise the cached session tenant, then first
 * membership. Used on authenticated global routes (e.g. `/whats-new`) where
 * the path has no `/~/slug` segment.
 */
export function resolveActiveTenantSlug(
  tenants?: TenantRoutingEntry[],
  pathname?: string | null
): string | null {
  const routeSlug =
    pathname != null
      ? readTenantSlugFromPath(pathname)
      : readTenantSlugFromCurrentUrl();
  return resolveLandingTenantSlug(routeSlug, tenants, cachedSessionTenantId);
}

/** Canonical tenant id for the active slug from {@link resolveActiveTenantSlug}. */
export function resolveActiveTenantId(
  tenants?: TenantRoutingEntry[],
  pathname?: string | null
): string | null {
  const slug = resolveActiveTenantSlug(tenants, pathname);
  return slug ? resolveSlugToTenantId(slug, tenants) : null;
}

/**
 * Tenant-preserved `/~/slug` for home navigation. Landing page is chosen by
 * `defaultLandingLoader` after navigation. URL slug when present; otherwise
 * first membership; `'/'` when none.
 */
export function getTenantPreservedRoot(
  tenants?: TenantRoutingEntry[],
  pathname?: string | null
): string {
  const slug = resolveNavigationTenantSlug(tenants, pathname);
  return slug ? buildTenantRootPath(slug) : '/';
}
